import { Box, Drawer, Grid, Typography } from '@mui/material'
import {
  useLoadOrderProduct,
  useLoadProductWeight,
  useLoadProposalProduct
} from 'modules/orders/hooks'
import { MdKeyboardArrowLeft } from 'react-icons/md'
import { GridListItem, IconButton } from '..'
import { calculeDiscount, formatCurrency, isDivision60User } from '../../utils'
import { get } from 'lodash'
import BigNumber from 'bignumber.js'
import { useMemo } from 'react'
import { ItemAttachments } from 'modules/orders/components/Form/items/ItemForm/ItemAttachments'
import Tracking from 'modules/orders/components/Tracking/Tracking'
import { useTrackingModalStore } from 'modules/orders/components/Tracking/trackingModalStore'
import { useAuth } from 'lib/auth'

export type ItemDetailDrawerProp = {
  item: any
  open: boolean
  onClose: () => void
  order?: any
}

const detailsGeneral: any = [
  {
    label: 'ICMS',
    key: 'icmsPercent',
    transform: (value: any) => `${value}%`
  },
  {
    label: 'IPI',
    key: 'ipiPercent',
    transform: (value: any) => `${value}%`
  }
  // {
  //   label: 'ST',
  //   key: 'stPercent',
  //   transform: (value: any) => `${value}%`
  // }

  // {
  //   label: 'Frete',
  //   key: 'freightPercent',
  //   transform: (value: any) => `${value}%`
  // }
]

const weights: any = [
  // {
  //   label: 'Líquido',
  //   key: 'product.netWeight',
  //   transform: (value: any, item: any) =>
  //     `${value}KG | ${value * +item.quantity}KG`
  // },
  {
    label: 'Bruto',
    key: 'grossWeight',
    transform: (value: any, item: any) =>
      `${value.toFixed(2)}KG | ${(value * +item.quantity).toFixed(2)}KG`
  },
  {
    label: 'Valor por KG',
    key: 'hasWeightPrice',
    transform: (value: any, item: any) =>
      value
        ? formatCurrency(+item.salePrice / +item.grossWeight)
        : 'não definido'
  }
]

export const ItemDetailDrawer = ({
  item,
  open,
  order,
  onClose
}: ItemDetailDrawerProp) => {
  const isTrackingOpen = useTrackingModalStore.useIsModalOpen()
  const { user } = useAuth()
  const canSeeCommission = isDivision60User(user?.division)

  const { data: productReq } = useLoadOrderProduct({
    productId: item?.product?.id,
    customerId: order?.customer?.id,
    invoiceTypeId: order?.orderType?.id,
    unityId: order?.unity?.id
  })

  const { data: taxesReq } = useLoadProposalProduct({
    productId: item?.product?.id,
    customerId: order?.customer?.id,
    invoiceTypeId: order?.orderType?.id,
    unityId: order?.unity?.id,
    proposalId: order?.orderId || order?.proposalId,
    sequence: item.sequence,
    mode: order?.orderId ? 'order' : 'proposal'
  })

  const { data: grossWeight } = useLoadProductWeight({
    productId: item?.product?.id,
    identifyId: item?.configurationId
  })

  const commissionValue = useMemo(
    () =>
      BigNumber(taxesReq?.data?.commissionPercent || '0')
        .times(0.01)
        .times(item?.salePrice || 0)
        .times(item?.quantity || 0)
        .toNumber(),
    [taxesReq?.data?.commissionPercent, item?.salePrice, item?.quantity]
  )

  const itemAttachData = useMemo(
    () => ({
      customerId: order?.customer?.id,
      orderId: order?.orderId || order?.proposalId,
      subject: `Web Ped: ${order?.orderId || order?.proposalId} Mat.: ${
        item?.product?.id
      } Conf.: ${item?.configurationId}`,
      type: 'I',
      title: {
        id: '502',
        name: 'ACOMPANHAMENTO DO ITEM'
      },
      item: {
        id: item?.product?.id,
        configurationId: item?.configurationId,
        sequence: item?.sequence
      }
    }),
    [
      item?.configurationId,
      item?.product?.id,
      item?.sequence,
      order?.customer?.id,
      order?.orderId,
      order?.proposalId
    ]
  )

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      {isTrackingOpen && <Tracking />}
      <Box sx={{ width: { md: 400 }, padding: { xs: 1, md: 3 } }}>
        <div
          style={{
            position: `sticky`,
            top: 0,
            backgroundColor: 'white',
            padding: 8,
            zIndex: 99,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 24
          }}
        >
          <IconButton onClick={() => onClose()}>
            <MdKeyboardArrowLeft
              size={24}
              color="#565656"
              style={{ background: '' }}
            />
          </IconButton>

          <Typography
            style={{
              marginLeft: '1rem',
              fontSize: '12',
              fontWeight: 550
            }}
          >
            {productReq?.data?.product?.name ||
              item?.product?.name ||
              'Sem Descrição'}
          </Typography>
        </div>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="caption">Configuração</Typography>
            <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={2}>
              {item?.configurationDescription}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption">N° Ordem de Compra</Typography>
            <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={2}>
              {taxesReq?.data?.xPed || '-'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption">Seq. Itens na OC</Typography>
            <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={2}>
              {taxesReq?.data?.nItemPed || '-'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption">Preço Formado</Typography>
            <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={2}>
              {formatCurrency(item?.defaultPrice)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption">Preço Unitário</Typography>
            <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={2}>
              {formatCurrency(item?.salePrice)}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="caption">Desconto (%)</Typography>
            <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={2}>
              {calculeDiscount(
                item?.salePrice,
                item?.defaultPrice
              )?.[0]?.toFixed(2) || 0}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="caption">Quantidade</Typography>
            <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={2}>
              {item?.quantity} {item?.product?.unitMeasure}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="caption">Quantidade Peças</Typography>
            <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={2}>
              {item?.quantityUn} UN.
            </Typography>
          </Grid>
        </Grid>

        <ItemAttachments data={itemAttachData} isView={!!order?.orderId} />

        {canSeeCommission && (
          <>
            <div style={{ margin: '24px 0px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                Comissão
              </div>
            </div>
            <Grid item xs={12} container>
              <GridListItem
                key={'2'}
                label={'Comissão'}
                value={`${formatCurrency(commissionValue)} (${
                  Number(taxesReq?.data?.commissionPercent).toFixed(2) || 0
                }%)`}
              />
            </Grid>
          </>
        )}

        <div style={{ margin: '24px 0px' }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Peso</div>
        </div>
        <Grid item xs={12} container>
          {weights.map((detail: any) => (
            <GridListItem
              key={detail.key}
              label={detail?.label}
              value={get(
                {
                  ...item,
                  grossWeight: grossWeight?.data?.weight || 0,
                  hasWeightPrice: grossWeight?.data?.hasWeightPrice || 0
                },
                detail.key
              )}
              transform={(value) =>
                detail?.transform(value, {
                  ...item,
                  grossWeight: grossWeight?.data?.weight || 0,
                  hasWeightPrice: grossWeight?.data?.hasWeightPrice
                })
              }
            />
          ))}
        </Grid>

        <div style={{ margin: '24px 0px' }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Impostos</div>
        </div>

        <Grid item xs={12} container>
          {detailsGeneral.map((detail: any) => (
            <GridListItem
              key={detail.key}
              label={detail?.label}
              value={get(taxesReq?.data, detail.key)}
              transform={detail?.transform}
            />
          ))}
        </Grid>

        <div style={{ margin: '24px 0px' }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
            Informações Adicionais
          </div>
        </div>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="caption">Unidade de Medida</Typography>
            <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={2}>
              {item?.product?.unitMeasure}
            </Typography>
          </Grid>
          {/* <Grid item xs={6}>
            <Typography variant="caption">Peso</Typography>
            <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={2}>
              {productReq?.data?.product?.netWeight || 0} KG
            </Typography>
          </Grid> */}
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="caption">Observação PCP</Typography>
            <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={2}>
              {item?.pcpObservation || '-'}
            </Typography>
          </Grid>
        </Grid>

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="h6">Total</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6">
              {formatCurrency(item?.salePrice * item?.quantity)}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  )
}
